import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Gift, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const LOYALTY = {
  premium: ["Premium or fine dining", "Surprise and delight, never points or stamps, because that feels transactional at this level.",
    ["Remember names, preferences and occasions, and note them in your booking system", "Give regulars early access to new menus, chef's tables and events", "Unexpected upgrades: a glass of something, an extra course, a kitchen visit", "A handwritten note after a special occasion"],
    ["Pick your 20 best regulars and start a guest notes card for each", "Brief the team on one surprise-and-delight moment per service", "Send a handwritten note after the next special occasion", "Send an insider early-access invite before your next menu or event", "Review your guest notes monthly and track how often regulars return"]],
  mid: ["Mid-market or casual dining", "A structured programme with a mechanic guests understand in one sentence.",
    ["Visit X times, get Y (e.g. your 5th visit gets a bottle of house wine)", "Birthday treat for members, with email sign-up at the table or by QR", "Keep it simple, consistent and genuinely rewarding", "Track repeat-visit rate monthly"],
    ["Write your one-sentence loyalty promise", "Put a sign-up QR or card on every table", "Set up the birthday email before launch", "Train the team to mention it in one line at the end of the meal", "Track your repeat-visit rate monthly"]],
  qsr: ["Casual, café or QSR", "Points or a digital stamp card: frictionless and volume-driven.",
    ["Digital stamp card (e.g. every 9th coffee free) with QR, no forms", "Use bonus stamps to nudge quiet times", "Reward frequency, not spend", "Push one simple message a week to card holders"],
    ["Choose a QR stamp card tool with no forms", "Put the QR on the counter, cups or bags", "Train the team on a one-line ask at the till", "Run bonus stamps in your quietest slot", "Send one simple message a week to card holders"]],
};

export const useLoyalty = () => {
  const [l, setL] = useState(null);
  useEffect(() => { api.get("/me/loyalty").then((r) => setL(r.data)).catch(() => setL({ type: null, done: [] })); }, []);
  const save = async (type, done) => {
    const prev = l;
    setL({ ...l, type, done });
    try { await api.put("/me/loyalty", { type, done }); } catch (e) { setL(prev); toast.error(errMsg(e)); }
  };
  return [l, save];
};

export const LoyaltySteps = ({ type, done, onChange }) => (
  <ul className="space-y-1" data-testid="loyalty-steps">
    {LOYALTY[type][3].map((s, i) => {
      const on = done.includes(i);
      return (
        <li key={s}>
          <button onClick={() => onChange(on ? done.filter((x) => x !== i) : [...done, i])} data-testid={`loyalty-step-${i}`}
            className="w-full flex items-start gap-2 text-left text-sm rounded-lg px-2 py-1.5 hover:bg-white transition-colors">
            <span className={`mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center ${on ? "bg-leaf border-leaf text-white" : "border-navy/30"}`}>{on && <Check className="h-3 w-3" />}</span>
            <span className={on ? "line-through text-muted-foreground" : ""}>{s}</span>
          </button>
        </li>
      );
    })}
  </ul>
);

export const LoyaltyDashCard = () => {
  const { user } = useAuth();
  const [l] = useLoyalty();
  if (!["hospitality", "both"].includes(user?.audience) || !l) return null;
  const steps = l.type ? LOYALTY[l.type][3] : [];
  const pct = steps.length ? Math.round((l.done.length / steps.length) * 100) : 0;
  return (
    <Link to="/app/guides?tab=loyalty" data-testid="dashboard-loyalty-card" className="fade-up mb-5 block rounded-2xl border border-border bg-card p-6 hover:border-pinkb/50 transition-colors">
      <div className="flex items-center justify-between gap-4">
        <div className="eyebrow flex items-center gap-2"><Gift className="h-3.5 w-3.5" />Loyalty builder{l.type && ` · ${LOYALTY[l.type][0]}`}</div>
        {l.type && <span className="text-xs font-mono" data-testid="loyalty-progress">{l.done.length}/{steps.length} steps</span>}
      </div>
      {!l.type ? <p className="mt-2 font-serif text-xl text-navy">Turn first visits into regulars. Pick your venue type to get your loyalty steps.</p> : (
        <>
          <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-leaf transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
          {l.done.length > 0 ? (
            <ul className="mt-4 space-y-1 text-sm" data-testid="loyalty-wins">{l.done.map((i) => <li key={i} className="flex gap-2"><Trophy className="h-4 w-4 text-sun shrink-0" />{steps[i]}</li>)}</ul>
          ) : <p className="mt-3 text-sm text-muted-foreground">No steps ticked yet. Your first win: {steps[0].toLowerCase()}.</p>}
        </>
      )}
    </Link>
  );
};
